import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import moment from "moment";

export const exportToXLSX = (data, filename) => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Registration-Data");
  XLSX.writeFile(wb, filename);
};

export const exportToPDF = (formData, filename) => {
  if (formData.gradeLevel) {
    exportPDFWithStudentPreferences(formData, filename);
  } else {
    exportPDFWithoutStudentPreferences(formData, filename);
  }
};

const exportPDFWithStudentPreferences = (formData, filename) => {
  const doc = new jsPDF();
  let yPosition = 10;

  doc.setFontSize(16); // Set font size for top heading
  doc.setFont("helvetica", "bold");
  doc.text(
    10,
    yPosition,
    `Enrollment Registration Form of ${formData.firstName} ${formData.lastName}`
  );
  yPosition += 15; // Increase yPosition after top heading

  // Define headings for each section
  const sectionHeadings = {
    "Learning Center Information": [
      "learningCenterId",
      "learningCenterName",
      "learningCenterCharter",
      "learningCenterAddress",
    ],
    "Registration Notes": ["generalRegistrationNotes", "createdAt"],
    "Special Programs": ["programs", "programDescription"],
    "Student Information": [
      "firstName",
      "middleName",
      "lastName",
      "haveSuffix",
      "suffix",
      "gender",
      "birthDate",
      "age",
      "gradeLevel",
      "studentEmail",
      "studentPrimaryPhone",
      "studentPrimaryPhoneType",
    ],

    "Student Preferences": ["studentName", "preferredpronouns"],

    "Parent Information": [
      "pgHighestLevelOfEducation",
      "permissionParentContact",
      "pgRelationshipToStudent",
      "pgFirstName",
      "pgLastName",
      "pgPrimaryPhone",
      "pgPrimaryPhoneType",
      "pgPrimaryEmail",
      "liveWithPg",
    ],
    "Parent Information 2": [
      "pg2RelationshipToStudent",
      "pg2HighestLevelOfEducation",
      "pg2FirstName",
      "pg2LastName",
      "pg2PrimaryPhoneType",
      "pg2PrimaryPhone",
      "pg2PrimaryEmail",
      "liveWithPg2",
    ],

    "Physical Address": [
      "street",
      "city",
      "state",
      "zip",
      "county",
      "isMailingAddressTheSame",
    ],
    "Mailing Address": [
      "mailingStreet",
      "mailingCity",
      "mailingState",
      "mailingZip",
    ],

    Ethnicity: ["isStudentHispanicOrLatino", "primaryRaceCategory"],
    "Previous School Information": [
      "previousSchool",
      "previousSchoolCity",
      "previousSchoolState",
    ],
    "Primary Language": ["primaryLanguage"],

    "Release Requests": [
      "releasePermissionToTextStudent",
      "releasePermissionToTextPg",
      "releaseAllowStudentAccessToGoogleAppsForEducation",
    ],
    "Parent Custody Information": [
      "custodyIssueExists",
      "custodyIssueNotes",
      "legalRestrictionsForAnyParent",
      "legalRestrictionsNotes",
    ],
  };

  const formatFieldName = (fieldName) => {
    let formattedName = fieldName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());
    formattedName = formattedName.replace(/Pg/g, "Parent");
    formattedName = formattedName.replace(/Pg2/g, "Parent2");
    formattedName = formattedName.replace(/\bPhone\b/g, "Phone Number");
    formattedName = formattedName.replace(
      /\bPhone Number Type\b/g,
      "Phone Type"
    );
    formattedName = formattedName.replace(
      /\bPreferredpronouns\b/g,
      "Preferred Pronouns"
    );
    if (fieldName === "county") {
      return "Country";
    }
    if (fieldName === "studentName") {
      return "Student Preferred Name";
    }
    if (fieldName === "legalRestrictionsForAnyParent") {
      return "Any Legal Restrictions for Parents";
    }
    return formattedName;
  };

  // Iterate over sectionHeadings object
  Object.entries(sectionHeadings).forEach(([heading, fields]) => {
    if (heading === "Mailing Address" && formData.isMailingAddressTheSame) {
      return; // Skip this section
    }
    // Add heading to the PDF
    doc.setFontSize(12); // Set font size for headings
    doc.setFont("helvetica", "bold");
    doc.text(15, yPosition, heading);
    yPosition += 10;

    fields.forEach((fieldName) => {
      if (fieldName === "suffix" && !formData.haveSuffix) {
        return;
      }

      if (fieldName === "custodyIssueNotes" && !formData.custodyIssueExists) {
        return;
      }

      // Skip "legalRestrictionsNotes" field if "legalRestrictionsForAnyParent" is set to "No"
      if (
        fieldName === "legalRestrictionsNotes" &&
        !formData.legalRestrictionsForAnyParent
      ) {
        return;
      }

      const fieldValue = formData[fieldName];

      // Check if fieldValue exists and add it to the PDF
      if (fieldValue !== undefined) {
        // Check if there's enough space for the current field
        if (yPosition + 20 >= doc.internal.pageSize.height) {
          doc.addPage(); // Add a new page if necessary
          yPosition = 15; // Reset yPosition
        }

        // Set font size and style for field values
        doc.setFontSize(12); // Set font size for field values
        doc.setFont("helvetica", "normal");

        // Convert boolean value to "Yes" or "No"
        const displayValue =
          typeof fieldValue === "boolean"
            ? fieldValue
              ? "Yes"
              : "No"
            : fieldValue;

        // Format birthDate field if it's present
        const formattedValue =
          fieldName === "birthDate"
            ? moment(fieldValue).format("MM/DD/YYYY")
            : displayValue;

        // Skip mailing address fields if isMailingAddressTheSame is true
        if (
          fieldName.startsWith("mailing") &&
          formData.isMailingAddressTheSame
        ) {
          return; // Skip this field
        }

        // Handle programs object separately
        if (fieldName === "programs" && typeof fieldValue === "object") {
          let programsString = ""; // Initialize an empty string to store programs information

          // Iterate through each program entry
          Object.entries(fieldValue).forEach(
            ([programName, programDetails]) => {
              const programValue = programDetails.unchecked
                ? "unchecked"
                : "checked";
              programsString += `${programName}: ${programValue} `;
            }
          );

          // Add programs information to the PDF
          doc.text(15, yPosition, `Programs: ${programsString}`);
          yPosition += 10;
        } else {
          // Add regular field
          doc.text(
            15,
            yPosition,
            `${formatFieldName(fieldName)}: ${formattedValue}`
          );
          yPosition += 10;
        }
      }
    });
    // Add spacing after each section
    yPosition += 8;
  });

  doc.save(filename);
};

const exportPDFWithoutStudentPreferences = (formData, filename) => {
  const doc = new jsPDF();
  let yPosition = 10;

  doc.setFontSize(16); // Set font size for top heading
  doc.setFont("helvetica", "bold");
  doc.text(
    10,
    yPosition,
    `Enrollment Registration Form of ${formData.firstName} ${formData.lastName}`
  );
  yPosition += 15; // Increase yPosition after top heading

  // Define headings for each section
  const sectionHeadings = {
    "Learning Center Information": [
      "learningCenterId",
      "learningCenterName",
      "learningCenterCharter",
      "learningCenterAddress",
    ],
    "Registration Notes": ["generalRegistrationNotes", "createdAt"],
    "Special Programs": ["programs", "programDescription"],
    "Student Information": [
      "firstName",
      "middleName",
      "lastName",
      "haveSuffix",
      "suffix",
      "gender",
      "birthDate",
      "age",
      "birthState",
      "birthCountry",
      "studentEmail",
      "studentPrimaryPhone",
      "studentPrimaryPhoneType",
    ],

    "Parent Information": [
      "pgHighestLevelOfEducation",
      "permissionParentContact",
      "pgRelationshipToStudent",
      "pgFirstName",
      "pgLastName",
      "pgPrimaryPhone",
      "pgPrimaryPhoneType",
      "pgPrimaryEmail",
    ],

    "Physical Address": [
      "liveWithPg",
      "livingStatus",
      "street",
      "city",
      "state",
      "zip",
      "county",
      "isMailingAddressTheSame",
    ],

    "Mailing Address": [
      "mailingStreet",
      "mailingCity",
      "mailingState",
      "mailingZip",
    ],
    Ethnicity: ["isStudentHispanicOrLatino", "primaryRaceCategory"],
    "Previous School Information": [
      "previousSchool",
      "previousSchoolCity",
      "previousSchoolState",
    ],
    "Primary Language": ["primaryLanguage"],
    "Home Language Survey": [
      "firstLanguage",
      "homeLanguage",
      "languageSpokenByParentsToStudent",
      "languageSpokenByAdultsAtHome",
    ],
    "Migrant Education": [
      "isPgEmployedInOneOrMoreAgriculturalOrFishingActivities",
    ],
    "Release Requests": [
      "releaseAudioAndVideo",
      "releasePermissionForSocialMedia",
      "releaseUseStudentWork",
      "releasePermissionToTextStudent",
      "releasePermissionToTextPg",
      "releaseAllowStudentAccessToGoogleAppsForEducation",
    ],
    "DASS Information": [
      "beenExpelled",
      "expelledFromWhichSchool",
      "expelledWhatYear",
      "beenSuspended",
      "suspendedFromWhichSchool",
      "suspendedWhatYear",
      "onProbation",
      "isParentOrExpecting",
      "moreThanThreeUnexcusedAbsences",
      "lateToSchoolMoreThanThreeTimes",
      "orderedToAttendSchool",
      "orderedToAttendWhichSchool",
      "orderedToAttendWhatYear",
      "retainedMoreThanOnce",
      "retainedFromWhichSchool",
      "retainedFromWhatYear",
      "droppedOutOfSchool",
      "droppedOutFromWhichSchool",
      "droppedOutFromWhatYear",
      "behindSemesterInCredits",
      "outOfSchoolMoreThanFortyFiveDays",
      "expelledMoreThanTwoSchoolsPastYear",
      "fosterCare",
      "youthExperiencingHomelessness",
    ],
    "Home Survey": ["accessToComputerAtHome", "accessToTheInternetAtHome"],
    "Health Conditions": [
      "knownHealthConditions",
      "asthma",
      "beeStingAllergy",
      "diabetes",
      "epilepsy",
      "heartCondition",
      "seizures",
      "nutAllergy",
      "knownHearingConditions",
      "wearsHearingAid",
      "knownEyeCondition",
      "glassesToBeWornAtAllTimes",
      "wearsContactLenses",
      "wearsGlasses",
      "medications",
      "otherHealthCondition",
      "medicalInstructions",
      "heathInsuranceCarrier",
      "insuranceIdOrPolicyNumber",
      "hospitalPreference",
    ],
    "Parent Custody Information": [
      "custodyIssueExists",
      "custodyIssueNotes",
      "legalRestrictionsForAnyParent",
      "legalRestrictionsNotes",
    ],
    "Emergency Contacts": [
      "emergencyContact1FirstName",
      "emergencyContact1LastName",
      "emergencyContact1Phone",
      "emergencyContact1Relationship",
      "authorizedForPickup1",
      "emergencyContact2FirstName",
      "emergencyContact2LastName",
      "emergencyContact2Phone",
      "emergencyContact2Relationship",
      "authorizedForPickup2",
    ],
  };

  const formatFieldName = (fieldName) => {
    let formattedName = fieldName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());
    formattedName = formattedName.replace(/Pg/g, "Parent");
    formattedName = formattedName.replace(/Pg2/g, "Parent2");
    formattedName = formattedName.replace(/\bPhone\b/g, "Phone Number");
    formattedName = formattedName.replace(
      /\bPhone Number Type\b/g,
      "Phone Type"
    );
    formattedName = formattedName.replace(
      /\bPreferredpronouns\b/g,
      "Preferred Pronouns"
    );
    if (fieldName === "county") {
      return "Country";
    }

    if (fieldName === "legalRestrictionsForAnyParent") {
      return "Any Legal Restrictions for Parents";
    }

    if (
      fieldName === "isPgEmployedInOneOrMoreAgriculturalOrFishingActivities"
    ) {
      formattedName =
        "Is Parent in one or more Agricultural or Fishing Activities on a Seasonal or Other Temporary Basis";
    }
    if (fieldName === "knownHearingConditions") {
      return "Known Hearing Problems";
    }
    return formattedName;
  };

  // Iterate over sectionHeadings object
  Object.entries(sectionHeadings).forEach(([heading, fields]) => {
    if (heading === "Mailing Address" && formData.isMailingAddressTheSame) {
      return; // Skip this section
    }
    // Add heading to the PDF
    doc.setFontSize(12); // Set font size for headings
    doc.setFont("helvetica", "bold");
    doc.text(15, yPosition, heading);
    yPosition += 10;

    fields.forEach((fieldName) => {
      if (fieldName === "suffix" && !formData.haveSuffix) {
        return;
      }

      if (fieldName === "custodyIssueNotes" && !formData.custodyIssueExists) {
        return;
      }

      // Skip "legalRestrictionsNotes" field if "legalRestrictionsForAnyParent" is set to "No"
      if (
        fieldName === "legalRestrictionsNotes" &&
        !formData.legalRestrictionsForAnyParent
      ) {
        return;
      }

      if (
        fieldName === "expelledFromWhichSchool" ||
        fieldName === "expelledWhatYear"
      ) {
        if (!formData.beenExpelled) {
          return;
        }
      }

      if (
        fieldName === "suspendedFromWhichSchool" ||
        fieldName === "suspendedWhatYear"
      ) {
        if (!formData.beenSuspended) {
          return;
        }
      }

      if (
        fieldName === "orderedToAttendWhichSchool" ||
        fieldName === "orderedToAttendWhatYear"
      ) {
        if (!formData.orderedToAttendSchool) {
          return;
        }
      }

      if (
        fieldName === "retainedFromWhichSchool" ||
        fieldName === "retainedFromWhatYear"
      ) {
        if (!formData.retainedMoreThanOnce) {
          return;
        }
      }

      if (
        fieldName === "droppedOutFromWhichSchool" ||
        fieldName === "droppedOutFromWhatYear"
      ) {
        if (!formData.droppedOutOfSchool) {
          return;
        }
      }
      if (fieldName === "wearsHearingAid" && !formData.knownHearingConditions) {
        return;
      }

      const fieldValue = formData[fieldName];

      // Check if fieldValue exists and add it to the PDF
      if (fieldValue !== undefined) {
        // Check if there's enough space for the current field
        if (yPosition + 20 >= doc.internal.pageSize.height) {
          doc.addPage(); // Add a new page if necessary
          yPosition = 15; // Reset yPosition
        }

        // Set font size and style for field values
        doc.setFontSize(12); // Set font size for field values
        doc.setFont("helvetica", "normal");

        // Convert boolean value to "Yes" or "No"
        const displayValue =
          typeof fieldValue === "boolean"
            ? fieldValue
              ? "Yes"
              : "No"
            : fieldValue;

        // Format birthDate field if it's present
        const formattedValue =
          fieldName === "birthDate"
            ? moment(fieldValue).format("MM/DD/YYYY")
            : displayValue;

        // Skip mailing address fields if isMailingAddressTheSame is true
        if (
          fieldName.startsWith("mailing") &&
          formData.isMailingAddressTheSame
        ) {
          return; // Skip this field
        }

        // Handle programs object separately
        if (fieldName === "programs" && typeof fieldValue === "object") {
          let programsString = ""; // Initialize an empty string to store programs information

          // Iterate through each program entry
          Object.entries(fieldValue).forEach(
            ([programName, programDetails]) => {
              const programValue = programDetails.unchecked
                ? "unchecked"
                : "checked";
              programsString += `${programName}: ${programValue} `;
            }
          );

          // Add programs information to the PDF
          doc.text(15, yPosition, `Programs: ${programsString}`);
          yPosition += 10;
        } else {
          // Add regular field
          doc.text(
            15,
            yPosition,
            `${formatFieldName(fieldName)}: ${formattedValue}`
          );
          yPosition += 10;
        }
      }
    });
    // Add spacing after each section
    yPosition += 8;
  });

  doc.save(filename);
};
